/**
 * Los envios que se hacen directo en WebShip, traidos para facturarlos (Mike,
 * 10-oct: "It's already running in webship... I need the billing in place").
 * Ver backend/services/webship_import.py.
 *
 * Se registra todo (Mike: "We must log every shipment into our own database"),
 * pero solo se factura desde la fecha "bill from", para no cobrar meses de
 * envios viejos. Los de la cuenta maestra llegan sin comerciante y no se
 * facturan hasta que el admin los asigna.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, RefreshCw, Save, ShieldAlert, Truck, UserCheck } from 'lucide-react';
import { apiErrorMessage } from '@/lib/apiError';
import { formatMoney as money } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebShipImportPanel({ token, accounts, onChange }) {
  const [cfg, setCfg] = useState(null);
  const [pend, setPend] = useState([]);
  const [marc, setMarc] = useState([]);
  const [notasRev, setNotasRev] = useState({});
  const [desde, setDesde] = useState('');
  const [nota, setNota] = useState('');
  const [elegido, setElegido] = useState({});
  const [notas, setNotas] = useState({});
  const [busy, setBusy] = useState(null);
  const [aviso, setAviso] = useState(null);

  const call = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API}${path}`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      ...opts,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(apiErrorMessage(d, `Request failed (${r.status}).`));
    return d;
  }, [token]);

  const cargar = useCallback(async () => {
    try {
      const [c, p, f] = await Promise.all([
        call('/webship/credit/import/config'), call('/webship/credit/import/pending'), call('/webship/credit/import/flagged'),
      ]);
      setMarc(f.shipments || []);
      setCfg(c);
      setDesde(c.bill_from || '');
      setPend(p.shipments || []);
      const sug = {};
      for (const s of p.shipments || []) if (s.suggested_user_id) sug[s.book_number] = s.suggested_user_id;
      setElegido((prev) => ({ ...sug, ...prev }));
    } catch (e) { setAviso({ type: 'err', text: e.message }); }
  }, [call]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarDesde = async () => {
    setBusy('cfg');
    try {
      await call('/webship/credit/import/config', { method: 'POST', body: JSON.stringify({ bill_from: desde || null, note: nota }) });
      setNota('');
      setAviso({ type: 'ok', text: desde ? `WebShip shipments from ${desde} on will be billed.` : 'Import turned off.' });
      await cargar();
    } catch (e) { setAviso({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const sincronizar = async () => {
    setBusy('sync');
    try {
      const r = await call('/webship/credit/import/run', { method: 'POST' });
      const t = (r.accounts || []).reduce((acc, a) => ({
        imported: acc.imported + (a.imported || 0), pending: acc.pending + (a.pending || 0),
        history: acc.history + (a.recorded_not_billable || 0), errors: acc.errors + (a.error ? 1 : 0),
      }), { imported: 0, pending: 0, history: 0, errors: 0 });
      setAviso({ type: t.errors ? 'err' : 'ok',
        text: `Recorded ${t.imported} shipment(s): ${t.history} before the billing date (not billed), ${t.pending} waiting for a merchant${t.errors ? `, ${t.errors} account(s) failed` : ''}.` });
      await cargar();
      onChange?.();
    } catch (e) { setAviso({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const asignar = async (s) => {
    setBusy(s.book_number);
    try {
      await call(`/webship/credit/import/${s.book_number}/assign`, {
        method: 'POST', body: JSON.stringify({ user_id: elegido[s.book_number], note: notas[s.book_number] || '' }),
      });
      setAviso({ type: 'ok', text: `Shipment ${s.book_number} assigned.` });
      await cargar();
      onChange?.();
    } catch (e) { setAviso({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const revisar = async (s) => {
    setBusy(`rev-${s.book_number}`);
    try {
      await call(`/webship/credit/import/${s.book_number}/review`, {
        method: 'POST', body: JSON.stringify({ note: notasRev[s.book_number] || '' }),
      });
      setAviso({ type: 'ok', text: `Shipment ${s.book_number} marked as reviewed.` });
      await cargar();
    } catch (e) { setAviso({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  if (!cfg) {
    // Si la configuracion no carga, el panel ya no desaparece: dice por que.
    return aviso?.type === 'err'
      ? <p className="text-sm text-red-600" data-testid="webship-import-error">{aviso.text}</p>
      : null;
  }
  const comerciantes = (accounts || []).filter((a) => a.role !== 'platform_admin');

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-4" data-testid="webship-import">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2"><Truck className="w-4 h-4" /> Shipments made in WebShip</h2>
          <p className="text-sm text-slate-500">
            Every shipment is recorded here every hour. From the billing date on, each one goes to its merchant's
            weekly invoice. Shipments from the SelleSend account wait here until you assign the merchant.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={sincronizar} disabled={busy === 'sync'} data-testid="webship-sync">
          {busy === 'sync' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sync now
        </Button>
      </div>

      {aviso && (
        <p className={`text-sm ${aviso.type === 'ok' ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="webship-import-notice">{aviso.text}</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs text-slate-500 mb-1">Bill from (shipment date)</span>
          <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="webship-bill-from" />
        </label>
        <label className="text-sm flex-1 min-w-[16rem]">
          <span className="block text-xs text-slate-500 mb-1">Why (required, it is logged)</span>
          <input className="input w-full" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="e.g. start billing WebShip shipments" data-testid="webship-bill-from-note" />
        </label>
        <Button size="sm" onClick={guardarDesde} disabled={busy === 'cfg' || !nota.trim()} data-testid="webship-bill-from-save">
          {busy === 'cfg' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
      </div>
      {!cfg.bill_from && (
        <p className="text-xs text-amber-700" data-testid="webship-import-off">
          Billing is off. Shipments are recorded but nobody is billed until you set the date. Shipments before that date are never billed.
        </p>
      )}

      {marc.length > 0 && (
        <div className="overflow-x-auto" data-testid="webship-flagged">
          <p className="text-sm font-medium mb-1 flex items-center gap-1.5 text-rose-700">
            <ShieldAlert className="w-4 h-4" /> Flagged by SelleSend risk rules ({marc.length})
          </p>
          <p className="text-xs text-slate-500 mb-2">
            These were made directly in WebShip and break a rule a SelleSend booking would have enforced. They already
            shipped, so they are not blocked: check each one and say what you found.
          </p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1 text-left">Book #</th>
                <th className="px-2 py-1 text-left">Merchant</th>
                <th className="px-2 py-1 text-left">Why</th>
                <th className="px-2 py-1 text-left">What you checked</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {marc.map((s) => (
                <tr key={s.book_number} className="border-t border-slate-100 dark:border-slate-800 align-top" data-testid={`flagged-${s.book_number}`}>
                  <td className="px-2 py-1 font-mono text-xs">{s.book_number}</td>
                  <td className="px-2 py-1">{(comerciantes.find((a) => a.user_id === s.user_id) || {}).company_name || s.user_id || 'Unassigned'}</td>
                  <td className="px-2 py-1">
                    <ul className="list-disc pl-4 space-y-0.5">{(s.risk_flags || []).map((m) => <li key={m.code}>{m.message}</li>)}</ul>
                  </td>
                  <td className="px-2 py-1">
                    <input className="input w-56" value={notasRev[s.book_number] || ''} onChange={(e) => setNotasRev((all) => ({ ...all, [s.book_number]: e.target.value }))} placeholder="e.g. called the merchant, approved" data-testid={`review-note-${s.book_number}`} />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" variant="outline" onClick={() => revisar(s)} disabled={busy === `rev-${s.book_number}` || !(notasRev[s.book_number] || '').trim()} data-testid={`review-${s.book_number}`}>
                      {busy === `rev-${s.book_number}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pend.length > 0 && (
        <div className="overflow-x-auto" data-testid="webship-pending">
          <p className="text-sm font-medium mb-2">Waiting for a merchant ({pend.length})</p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1 text-left">Book #</th>
                <th className="px-2 py-1 text-left">Date</th>
                <th className="px-2 py-1 text-left">Carrier</th>
                <th className="px-2 py-1 text-left">Sender</th>
                <th className="px-2 py-1 text-right">Carrier cost</th>
                <th className="px-2 py-1 text-left">Merchant</th>
                <th className="px-2 py-1 text-left">Why</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {pend.map((s) => (
                <tr key={s.book_number} className="border-t border-slate-100 dark:border-slate-800" data-testid={`pending-${s.book_number}`}>
                  <td className="px-2 py-1 font-mono text-xs">
                    {s.book_number}
                    {(s.shipper_reference || s.shipper_reference2) && (
                      <span className="block text-[11px] text-slate-500" data-testid={`ref-${s.book_number}`}>ref {[s.shipper_reference, s.shipper_reference2].filter(Boolean).join(' · ')}</span>
                    )}
                  </td>
                  <td className="px-2 py-1">{String(s.shipment_date || '').slice(0, 10)}</td>
                  <td className="px-2 py-1">{s.carrier_code} · {s.service_code}</td>
                  <td className="px-2 py-1">{s.sender?.company || s.sender?.name || '—'} {s.sender?.zip ? `· ${s.sender.zip}` : ''}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(s.quoted_amount)}</td>
                  <td className="px-2 py-1">
                    <select className="input" value={elegido[s.book_number] || ''} onChange={(e) => setElegido((all) => ({ ...all, [s.book_number]: e.target.value }))} data-testid={`assign-merchant-${s.book_number}`}>
                      <option value="">Choose…</option>
                      {comerciantes.map((a) => <option key={a.user_id} value={a.user_id}>{a.company_name || a.email || a.user_id}{a.user_id === s.suggested_user_id ? (s.suggested_by === 'reference' ? ' (suggested: reference code)' : ' (suggested)') : ''}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input className="input w-48" value={notas[s.book_number] || ''} onChange={(e) => setNotas((all) => ({ ...all, [s.book_number]: e.target.value }))} placeholder="e.g. sender matches" data-testid={`assign-note-${s.book_number}`} />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" onClick={() => asignar(s)} disabled={busy === s.book_number || !elegido[s.book_number] || !(notas[s.book_number] || '').trim()} data-testid={`assign-${s.book_number}`}>
                      {busy === s.book_number ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
